import clsx from 'clsx';

import { SDSSkeleton } from '@repo/sds/components/_internal/SDSSkeleton';
import { SDSFlexbox } from '@repo/sds/components/SDSFlexbox';
import type { SDSTextType } from '@repo/sds/components/SDSText';

import '@repo/sds/components/_internal/SDSSkeletonText/SDSSkeletonText.scss';

export interface SDSSkeletonTextProps {
  /** Text type to determine skeleton height */
  textType?: SDSTextType;
  /** Number of lines to display */
  lines?: number;
  /** Width of the skeleton (last line can be different) */
  width?: string | number;
  /** Width of the last line (for more natural paragraph appearance) */
  lastLineWidth?: string;
  /** Additional CSS classes */
  className?: string;
}

// Map text types to line-height CSS variables (for container height)
const TEXT_TYPE_TO_LINE_HEIGHT: Record<SDSTextType, string> = {
  title: '--line-height-xxx-large',
  h1: '--line-height-xx-large',
  h2: '--line-height-xx-large',
  h3: '--line-height-large',
  h4: '--line-height-x-large',
  h5: '--line-height-medium',
  'body-extra-large': '--line-height-medium',
  'body-large-bold': '--line-height-medium',
  'body-medium-large-bold': '--line-height-x-large',
  'body-large': '--line-height-regular',
  'body-medium-bold': '--line-height-regular-small',
  'body-medium': '--line-height-regular',
  'body-small': '--line-height-x-small',
  'button-x-large': '--line-height-large',
  'button-large': '--line-height-medium',
  'button-medium': '--line-height-small',
  'button-small': '--line-height-xx-small',
  tag: '--line-height-xx-small',
};

// Map text types to skeleton font-size CSS variables (for visible skeleton height)
const TEXT_TYPE_TO_SKELETON_VAR: Record<SDSTextType, string> = {
  title: '--font-size-skeleton-xxx-large',
  h1: '--font-size-skeleton-xx-large',
  h2: '--font-size-skeleton-x-large',
  h3: '--font-size-skeleton-large',
  h4: '--font-size-skeleton-medium-large',
  h5: '--font-size-skeleton-medium',
  'body-extra-large': '--font-size-skeleton-medium',
  'body-large-bold': '--font-size-skeleton-medium-small',
  'body-medium-large-bold': '--font-size-skeleton-medium-large',
  'body-large': '--font-size-skeleton-medium-small',
  'body-medium-bold': '--font-size-skeleton-small-medium',
  'body-medium': '--font-size-skeleton-small-medium',
  'body-small': '--font-size-skeleton-small',
  'button-x-large': '--font-size-skeleton-medium-large',
  'button-large': '--font-size-skeleton-medium',
  'button-medium': '--font-size-skeleton-small-medium',
  'button-small': '--font-size-skeleton-small',
  tag: '--font-size-skeleton-x-small',
};

// Get appropriate border radius based on text type
const getRadius = (textType: SDSTextType): 'small' | 'medium' => {
  if (textType.startsWith('h') || textType.startsWith('button')) {
    return 'medium';
  }
  return 'small';
};

/**
 * Skeleton component specifically designed for text content.
 * Handles single and multi-line text with proper sizing based on text type.
 *
 * Text skeletons are purely decorative.
 * For loading announcements, handle them at the page/section level.
 */
export function SDSSkeletonText({
  textType = 'body-medium',
  lines = 1,
  width = '100%',
  lastLineWidth = '70%',
  className,
}: SDSSkeletonTextProps) {
  const lineHeightVar = TEXT_TYPE_TO_LINE_HEIGHT[textType];
  const skeletonHeightVar = TEXT_TYPE_TO_SKELETON_VAR[textType];
  const radius = getRadius(textType);

  return (
    <div className={clsx('SDSSkeletonText__wrapper', className)} aria-hidden="true">
      {Array.from({ length: lines }).map((_, index) => {
        const isFirstLine = index === 0;
        const isLastLine = index === lines - 1;
        const isOnlyLine = lines === 1;

        // If it's the 1st line and only line → 100%
        // If it's the last line but not first line → 70%
        // Otherwise → 100%
        let lineWidth = width;
        if (!isOnlyLine && isLastLine && !isFirstLine) {
          lineWidth = lastLineWidth;
        }
        const key = `skeleton-${textType}-${lineWidth}-${index + 1}`;

        return (
          <SDSFlexbox
            key={key}
            alignItems="center"
            className="SDSSkeletonText__line"
            style={{
              height: `var(${lineHeightVar})`,
              width,
            }}
          >
            <SDSSkeleton
              width={lineWidth}
              height={`var(${skeletonHeightVar})`}
              radius={radius}
              className={clsx('SDSSkeletonText', `SDSSkeletonText--${textType}`)}
            />
          </SDSFlexbox>
        );
      })}
    </div>
  );
}
